import { createRef } from 'react';

import { Logger } from '../../models/logger';
import { FileUpload } from '../../models/file-upload';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { FileSelectEvent } from '../../types/file-upload.types';
import { PageData, RouteProps, SeoFeature } from '../../types/route-types';
import { FromTypes, ToTypes } from '../../types/types';
import { DateTimeUtils } from '../../ui-utils/date-time.utils';
import { getAudioCodecForExtension, getVideoCodecForExtension } from '../../utils/codec-registry';
import { getExtension } from '../../utils/file';
import { FileUploadUtils } from '../../utils/file-upoad.utils';
import { isNaN, isNumber, toNumber } from '../../utils/lodash-min';
import { MotiveLocationTypes } from '../../utils/request.utils';
import { SupportedFileTypes } from '../converter/file-upload-manager';
import FileUploadComponent from '../converter/file-upload/file-upload.component';
import Dropdown from '../ui/dropdown/dropdown';
import { FeatureDetailsComponent } from '../ui/feature-details/feature-details.component';
import FeaturesTilesComponent from '../ui/features-tiles/features-tiles.component';
import { FileListItemComponent } from '../ui/file-list/file-list-item.component';
import { MotiveComponent } from '../ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import RangeSelector, { RangeHandlerTypes, RangeSelectorResult } from '../ui/range-selector/range-selector';
import RatingDisplayComponent from '../ui/rating-display/rating-display.component';
import { RelatedLinksComponent } from '../ui/related-links/related-links.component';
import { AVBaseComponent, AVBaseState } from '../shared/av-base.component';
import styles from './av-trimmer.component.module.scss';


interface AVTrimmerState extends AVBaseState {
}


export default class AVTrimmerComponent extends AVBaseComponent<AVTrimmerState> {
    protected readonly logger = Logger.getLogger('AVTrimmerComponent');

    trimmedFileUploadArray: FileUpload[] = [];

    videoStart = 0;
    videoEnd: number;
    currentRange: RangeSelectorResult;

    readonly state: Readonly<AVTrimmerState> = {
        fileSelected: false,
        fakeUploadInProgressFileForSelectedFiles: [],
        fileWithMetadataList: [],
        isPlaying: false,
        isMuted: false,
        isLoading: false,
    };

    get isFileTrimInProgress(): boolean {
        return this.trimmedFileUploadArray && this.trimmedFileUploadArray.length > 0 && this.trimmedFileUploadArray.some(f => !f.isCompleted() && !f.isFailed());
    }

    playOrPauseOutputVideo() {
        if (this.outputVideoElement.paused) {
            this.outputVideoElement.play();
            const seekToBigginingFn = () => {
                if (this.outputVideoElement.currentTime < this.currentRange.start || this.outputVideoElement.currentTime > this.currentRange.end) {
                    this.seekOutputVideoToStartOfTheCut();
                }
                if (!this.outputVideoElement.paused) {
                    this.setState({ isPlaying: true });
                    setTimeout(() => seekToBigginingFn(), 10);
                } else {
                    this.setState({ isPlaying: false });
                }
            };
            seekToBigginingFn();
        } else {
            this.outputVideoElement.pause();
            this.setState({ isPlaying: false });
        }
    }

    seekOutputVideoToStartOfTheCut() {
        this.outputVideoElement.currentTime = this.currentRange.start;
        this.forceUpdate();
    }

    seekOutputVideo(seconds: number) {
        if (seconds < 0) {
            if (this.outputVideoElement.currentTime + seconds < this.currentRange.start) {
                this.outputVideoElement.currentTime = this.currentRange.start;
            } else {
                this.outputVideoElement.currentTime = this.outputVideoElement.currentTime + seconds;
            }
        } else if (seconds > 0) {
            if (this.outputVideoElement.currentTime + seconds > this.currentRange.end) {
                this.outputVideoElement.currentTime = this.currentRange.end;
            } else {
                this.outputVideoElement.currentTime = this.outputVideoElement.currentTime + seconds;
            }
        }
        this.forceUpdate();
    }

    public updateOutputVideoStartAndEnd(rangeValue: RangeSelectorResult) {
        const MIN_TRIM_DURATION = 0.1;
        if (isNaN(rangeValue.start) || isNaN(rangeValue.end)) return;

        let start = Math.max(0, Math.min(this.videoDuration, rangeValue.start));
        let end = Math.max(0, Math.min(this.videoDuration, rangeValue.end));

        if (end - start < MIN_TRIM_DURATION) {
            if (rangeValue.handlerType === RangeHandlerTypes.START) {
                start = Math.max(0, end - MIN_TRIM_DURATION);
            } else {
                end = Math.min(this.videoDuration, start + MIN_TRIM_DURATION);
            }
        }

        this.currentRange = { ...rangeValue, start, end };
        if (this.outputVideoElement) {
            this.outputVideoElement.currentTime = rangeValue.handlerType === RangeHandlerTypes.START ? start : end;
        }
        this.forceUpdate();
    }

    videoLoaded() {
        this.videoEnd = this.outputVideoElement.duration;
        this.videoStart = 0;
        this.videoDuration = this.outputVideoElement.duration;
        this.updateOutputVideoStartAndEnd({ start: this.videoStart, end: this.videoEnd, handlerType: RangeHandlerTypes.START });
        this.forceUpdate();
    }

    async trimAndDownload() {
        const MIN_TRIM_DURATION = 0.1;

        if (!this.currentRange || (this.currentRange.end - this.currentRange.start) < MIN_TRIM_DURATION) {
            this.uiContext.showErrorDialog({
                title: 'Error', text: 'Please select a valid trim range (minimum 0.1 seconds)'
            });
            return;
        }

        if (this.isFileTrimInProgress) {
            this.uiContext.warnToast(
                'Please wait', 'Please wait until current process to be finished', false, false
            );
            return;
        }

        const fileUpload = this.sourceFileUploadArray[0];
        const fmds = await FileUploadUtils.toUploadInProgressFile<SupportedFileTypes>(fileUpload);
        const fmd: UploadInProgressFile<SupportedFileTypes> = fmds[0];

        const extension = '.' + getExtension((this.sourceFileUploadArray[0].file as any).name);
        const outputExtension = this.selectedToType === 'same'
            ? extension.replace('.', '').toUpperCase()
            : this.selectedToType.replace('.', '').toUpperCase();

        const isAudio = this.pageData.fromType.indexOf(FromTypes.AUDIO) > -1;

        const trimOptions: Record<string, any> = isAudio ? {
            process: 'TRIM',
            toMedia: 'AUDIO',
            toExtension: outputExtension,
            audioCodec: getAudioCodecForExtension(outputExtension),
            compression: 'FILE_SIZE_PERCENTAGE',
            sizePercentage: 100,
            audioChannelSelection: 'AUDIO_CHANNEL',
            audioChannel: 'ORIGINAL',
            audioSampleRateSelection: 'AUDIO_SAMPLE_RATE',
            audioSampleRate: 'ORIGINAL',
        } : {
            process: 'TRIM',
            toMedia: 'VIDEO',
            toExtension: outputExtension,
            videoCodec: getVideoCodecForExtension(outputExtension),
            audioCodec: getAudioCodecForExtension(outputExtension),
            videoCompression: 'FILE_SIZE_PERCENTAGE_WITH_AUTO_SCALE',
            sizePercentage: 100,
            autoScale: false,
            videoResolution: 'VIDEO_RESOLUTION_ORIGINAL',
            videoTrimSelection: 'UNCHANGED',
        };

        trimOptions.outputTrim = {
            startTime: this.currentRange.start,
            duration: this.currentRange.end - this.currentRange.start,
        };

        const trimmedFileUpload = this.convertNoneMergeUploadInProgressFilesToFileUpload(fmd, trimOptions);
        this.trimmedFileUploadArray.splice(0, this.trimmedFileUploadArray.length);
        this.trimmedFileUploadArray.unshift(trimmedFileUpload);
        this.forceUpdate();
        setTimeout(() => {
            if (this.savedFilesRef.current) {
                this.savedFilesRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 100);
        await this.processFile(trimmedFileUpload, () => {});
    }

    updateCurrentTime(ev) {
        const value = toNumber(ev.target.value);
        if (isNumber(value) && !isNaN(value)) {
            this.outputVideoElement.currentTime = value;
        }
    }

    updateRangeSliderStartToCurrentTime() {
        const MIN_TRIM_DURATION = 0.1;
        const currentTime = this.outputVideoCurrentTime;
        const maxStart = this.currentRange.end - MIN_TRIM_DURATION;
        const newStart = Math.max(0, Math.min(currentTime, maxStart));
        this.videoStart = newStart;
        this.currentRange.start = newStart;
        this.forceUpdate();
    }

    updateRangeSliderEndToCurrentTime() {
        const MIN_TRIM_DURATION = 0.1;
        const currentTime = this.outputVideoCurrentTime;
        const minEnd = this.currentRange.start + MIN_TRIM_DURATION;
        const newEnd = Math.min(this.videoDuration, Math.max(currentTime, minEnd));
        this.videoEnd = newEnd;
        this.currentRange.end = newEnd;
        this.forceUpdate();
    }

    toggle() {
        this.videoStart = this.outputVideoCurrentTime;
    }

    valueFormatter(value: string) {
        return value;
    }

    private getSEOSections() {
        return <div className="mt-5">
            {
                <div className="mt-3" >
                    <FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent>
                </div>
            }
            {
                <div className="mt-3" >
                    <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
                </div>
            }
            {
                this.showRating && <div className="text-center mt-3">
                    <RatingDisplayComponent
                        rating={this.props.rating}
                        title={this.title}
                        feature={this.feature}
                        description={this.description}>
                    </RatingDisplayComponent>
                </div>
            }
            {
                this.showInfoGrid && !this.featureDescriptionsDisabled && <div className="mt-3">
                    {
                        this.features && <FeaturesTilesComponent features={this.features}></FeaturesTilesComponent>
                    }
                </div>
            }
        </div>;
    }

    getVideoAndControllers() {
        const currentTime = this.outputVideoCurrentTime || 0;
        const duration = this.videoDuration || 0;
        const progress = (currentTime / duration) * 100 || 0;

        return <div className={styles['preview-column']}>
            <button className={styles['start-new-btn']} onClick={e => this.startNew()}>
                <span>←</span> Start New
            </button>

            <div className={styles['video-container']}>
                {
                    this.state.isLoading && <div className={styles['loading-overlay']}>
                        <div className={styles['spinner']}></div>
                        <div className={styles['text']}>Processing Media...</div>
                        <div className={styles['sub-text']}>This may take a moment.</div>
                    </div>
                }
                {
                    !this.state.isLoading && !this.fileUrl && this.sourceFileUploadArray && this.sourceFileUploadArray.length > 0 && <div>
                        <FileListItemComponent
                            {...this.props}
                            fileUpload={this.sourceFileUploadArray[0]}>
                        </FileListItemComponent>
                    </div>
                }
                {
                    !this.state.isLoading && this.fileUrl && <div className={styles['video-wrapper']}>
                        <video ref={this.outputVideoRef}
                            onClick={() => this.playOrPauseOutputVideo()}
                            onTimeUpdate={() => this.forceUpdate()}
                            onEnded={() => this.setState({ isPlaying: false })}
                            onLoadedMetadata={e => this.videoLoaded()}>
                            <source src={this.fileObjectURL} />
                            <source src={this.fileUrl} />
                        </video>

                        <div className={styles['custom-controls']}>
                            <div className={styles['progress-container']}>
                                <input
                                    type="range"
                                    min="0"
                                    max={duration}
                                    step="0.1"
                                    value={currentTime}
                                    onChange={(e) => this.handleScrub(e)}
                                    className={styles['scrubber']}
                                    style={{ backgroundSize: `${progress}% 100%` } as any}
                                />
                            </div>
                            <div className={styles['control-buttons']}>
                                <button className={styles['control-icon']} onClick={() => this.playOrPauseOutputVideo()}>
                                    {this.state.isPlaying ?
                                        <img src="/icons/svg/pause.svg" alt="Pause" /> :
                                        <img src="/icons/svg/play.svg" alt="Play" />
                                    }
                                </button>
                                <div className={styles['time']}>
                                    {DateTimeUtils.secondsToTime(currentTime, true)} / {DateTimeUtils.secondsToTime(duration, true)}
                                </div>
                                <div className={styles['spacer']}></div>
                                <button className={styles['control-icon']} onClick={() => this.toggleMute()}>
                                    {this.state.isMuted ?
                                        <img src="/icons/svg/volume-mute.svg" alt="Unmute" /> :
                                        <img src="/icons/svg/volume-up.svg" alt="Mute" />
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>;
    }

    render() {
        if (!this.state.fileSelected) {
            return <div key="no-file" className={`trimmer-container ${styles['trimmer-container']}`}>
                <div className={styles['hero-section']}>
                    <div className={styles['hero-badge']}>
                        <span className={styles['badge-icon']}>✂️</span>
                        <span>Free Online Tool</span>
                    </div>
                    <h1 className={styles['hero-title']}>{this.title}</h1>
                    <p className={styles['hero-description']}>{this.description}</p>

                    <div className={styles['upload-card']}>
                        <div className={styles['upload-area']}>
                            <div className={styles['upload-icon']}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                            </div>
                            <div className={styles['upload-text']}>
                                <span className={styles['upload-title']}>Drop your file here, or browse</span>
                                <span className={styles['upload-subtitle']}>Supports MP4, MOV, AVI, MKV, WebM, MP3, WAV and more</span>
                            </div>
                            <FileUploadComponent
                                fileSelected={$event => this.fileSelected($event)}
                                singleFileSelect={true}
                                fileSelectButtonClicked={() => { }}
                                acceptedFileTypes={this.pageData?.accept}
                                uploadBtnText="Select File"
                                uploadBtnStyles={{}}
                                uploadBtnClassName={styles['upload-btn']}>
                            </FileUploadComponent>
                        </div>
                        <div className={styles['upload-footer']}>
                            <span>🔒 Your files are processed securely and never stored</span>
                        </div>
                    </div>

                    <div className={styles['features-grid']}>
                        <div className={styles['feature-item']}>
                            <div className={styles['feature-icon']}>⚡</div>
                            <div className={styles['feature-content']}>
                                <h3>Lightning Fast</h3>
                                <p>Process files in seconds with our optimized servers</p>
                            </div>
                        </div>
                        <div className={styles['feature-item']}>
                            <div className={styles['feature-icon']}>🎯</div>
                            <div className={styles['feature-content']}>
                                <h3>Precise Trimming</h3>
                                <p>Set exact start and end points with frame accuracy</p>
                            </div>
                        </div>
                        <div className={styles['feature-item']}>
                            <div className={styles['feature-icon']}>💎</div>
                            <div className={styles['feature-content']}>
                                <h3>No Quality Loss</h3>
                                <p>Maintain original quality with smart re-encoding</p>
                            </div>
                        </div>
                    </div>
                </div>

                {this.getSEOSections()}
            </div>
        }

        return <div key="has-file" className={`trimmer-container ${styles['trimmer-container']}`}>
            <div className="gcontent-container mb-4">
                <MotiveComponent
                    data={AdSlots.belowTool} containerWidth="100%" containerHeight="60px"
                    location={MotiveLocationTypes.convertPageBelowFeatureTitle} adWidth="720px" adHeight="60px" showAds={true}
                    className='width_major_md'
                ></MotiveComponent>
            </div>

            <div className={styles['editor-wrapper']}>
                {/* Left Column: Preview */}
                {this.getVideoAndControllers()}

                {/* Right Column: Controls */}
                <div className={styles['controls-column']}>

                    <div className={styles['control-group']}>
                        <label className={styles['group-title']}>Timeline & Playback</label>

                        <div className={styles['playback-controls']}>
                            <button disabled={!this.fileUrl} className={styles['control-btn']} onClick={e => this.seekOutputVideoToStartOfTheCut()} title="Reset to Start">
                                <img src="/icons/svg/arrow-counterclockwise.svg" alt="Reset" />
                            </button>
                            <button disabled={!this.fileUrl} className={styles['control-btn']} onClick={e => this.seekOutputVideo(-5)} title="-5 Seconds">
                                -5s
                            </button>
                            <button disabled={!this.fileUrl} className={`${styles['control-btn']} ${styles['play-btn']}`} onClick={e => this.playOrPauseOutputVideo()} title="Play/Pause">
                                <img src={this.outputVideoElement?.paused ? "/icons/svg/play.svg" : "/icons/svg/pause.svg"} alt="Play/Pause" />
                            </button>
                            <button disabled={!this.fileUrl} className={styles['control-btn']} onClick={e => this.seekOutputVideo(5)} title="+5 Seconds">
                                +5s
                            </button>
                        </div>

                        <div className="mb-3">
                            {
                                this.videoDuration && <RangeSelector
                                    valueFormatter={(e) => this.valueFormatter(e)}
                                    max={this.videoDuration}
                                    start={this.currentRange?.start || this.videoStart}
                                    end={this.currentRange?.end || this.videoEnd}
                                    step={0.01}
                                    onChange={$event => this.updateOutputVideoStartAndEnd($event)}>
                                </RangeSelector>
                            }
                        </div>
                    </div>

                    <div className={styles['control-group']}>
                        <label className={styles['group-title']}>Precision Trim</label>
                        <div className={styles['range-inputs']}>
                            <div className={styles['input-group']}>
                                <label>Start Time (s)</label>
                                <div className={styles['input-wrapper']}>
                                    <input
                                        type="number"
                                        value={this.currentRange?.start ?? 0}
                                        onChange={(e) => this.updateOutputVideoStartAndEnd({ ...this.currentRange, start: parseFloat(e.target.value), handlerType: RangeHandlerTypes.START })}
                                        step="0.1"
                                    />
                                    <button className={styles['set-current-btn']} onClick={() => this.updateRangeSliderStartToCurrentTime()} title="Set to current playhead position">
                                        Current
                                    </button>
                                </div>
                            </div>
                            <div className={styles['input-group']}>
                                <label>End Time (s)</label>
                                <div className={styles['input-wrapper']}>
                                    <input
                                        type="number"
                                        value={this.currentRange?.end ?? this.videoDuration}
                                        onChange={(e) => this.updateOutputVideoStartAndEnd({ ...this.currentRange, end: parseFloat(e.target.value), handlerType: RangeHandlerTypes.END })}
                                        step="0.1"
                                    />
                                    <button className={styles['set-current-btn']} onClick={() => this.updateRangeSliderEndToCurrentTime()} title="Set to current playhead position">
                                        Current
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles['control-group']}>
                        <label className={styles['group-title']}>Output Setting</label>
                        <div className={styles['format-selector']}>
                            <Dropdown
                                values={this.supportedAVTypes}
                                initialValue={this.supportedAVTypes.find(t => t.item === this.selectedToType)}
                                valueFormatter={(o) => o.text}
                                onSelect={(v) => this.updateToVideoType(v)}
                                hasTooltip={() => false}
                                getTooltip={() => ''}
                            />
                        </div>
                    </div>

                    <div className={styles['action-section']}>
                        <button className={styles['download-btn']} onClick={() => this.trimAndDownload()}>
                            {this.shouldShowLoading ? 'Processing...' : 'Trim & Download'}
                            {!this.shouldShowLoading && <img src="/icons/svg/download.svg" className="white-icon" width="20" height="20" alt="" style={{ filter: 'brightness(0) invert(1)' }} />}
                        </button>
                    </div>
                </div>
            </div>

            <div ref={this.savedFilesRef} className={styles['saved-files']}>
                {
                    this.trimmedFileUploadArray && this.trimmedFileUploadArray.length > 0 && <div className="mt-4">
                        <h4 className="mb-3">Processed Files</h4>
                        {this.trimmedFileUploadArray.map((file, i) => (
                            <FileListItemComponent key={file.id} {...this.props} fileUpload={file} />
                        ))}
                    </div>
                }
            </div>

        </div>;
    }

}
